import React, { useState, useEffect } from "react";
import {
    Box,
    Flex,
    Text,
    Icon,
    Divider,
    Stack,
    Input,
    InputGroup,
    InputRightAddon
} from "@chakra-ui/react";
import {
    Calendar,
    Box as BoxIcon,
    Layers,
    Clipboard,
    CalendarCheck,
} from "lucide-react";
import ReusableDrawer from "components/Drawers/ReusableDrawer";
import { formatDate } from "utils/dateFormatter";
import GradientButton from "components/Buttons/GradientButton";
import { useDispatch } from "react-redux";
import { updateSizeEstimateAction, fetchAllSizeEstimatesAction } from "state/actions/packageActions";
import { useCustomNotification } from "components/Toasts/OpenNotification";

const ViewSizeEstimates = ({ isOpen, onClose, estimate }) => {
    const dispatch = useDispatch();
    const showToast = useCustomNotification();

    const [isEditing, setIsEditing] = useState(false);
    const [loading, setLoading] = useState(false);
    const [localEstimate, setLocalEstimate] = useState(estimate);

    const [formValues, setFormValues] = useState({
        weight: 0,
        length: 0,
        width: 0,
        height: 0,
    });

    // Update local state when estimate prop changes
    useEffect(() => {
        if (estimate) {
            setLocalEstimate(estimate);
            setFormValues({
                weight: estimate?.weight || 0,
                length: estimate?.length || 0,
                width: estimate?.width || 0,
                height: estimate?.height || 0,
            });
        }
    }, [estimate]);

    const handleInputChange = (field, value) => {
        setFormValues(prev => ({ ...prev, [field]: value }));
    };

    const handleToggleEdit = async () => {
        if (!isEditing) {
            setIsEditing(true);
            return;
        }

        setLoading(true);
        try {
            const payload = {
                size: localEstimate.size_category,
                ...formValues,
            };

            const response = await dispatch(updateSizeEstimateAction(localEstimate.size_category, payload));

            if (response.success) {
                setLocalEstimate(prev => ({
                    ...prev,
                    ...formValues,
                    updated_at: new Date().toISOString()
                }));

                showToast({
                    message: "Updated successfully!",
                    type: "success",
                    duration: 3,
                });
                dispatch(fetchAllSizeEstimatesAction());
            } else {
                throw new Error(response.error || "Failed to update");
            }
        } catch (error) {
            showToast({
                message: "Failed to update.",
                description: error.message,
                type: "error",
                duration: 3,
            });
            // Revert to original values on error
            setFormValues({
                weight: localEstimate?.weight || 0,
                length: localEstimate?.length || 0,
                width: localEstimate?.width || 0,
                height: localEstimate?.height || 0,
            });
        } finally {
            setLoading(false);
            setIsEditing(false);
        }
    };

    const handleCloseDrawer = () => {
        setIsEditing(false);
        onClose();
    };

    if (!localEstimate) return null;

    return (
        <Flex direction="column" p={8} width="100%">
            <ReusableDrawer
                isOpen={isOpen}
                onClose={handleCloseDrawer}
                headerContent={
                    <Flex justifyContent="space-between" alignItems="center" width="100%">
                        <Text fontSize="lg" fontWeight="bold">View Size Estimate</Text>
                    </Flex>
                }
                bodyContent={
                    <Box mt={4}>
                        <Flex justifyContent="space-between" alignItems="center">
                            <Text fontSize="2xl" fontWeight="bold">
                                {localEstimate.size_category} Estimate
                            </Text>
                        </Flex>

                        <Flex mt={2} alignItems="center">
                            <Icon as={Calendar} size={16} color="gray.500" mr={1} />
                            <Text fontSize="sm" color="gray.500" mr={4}>
                                Created At: {formatDate(localEstimate.created_at)}
                            </Text>
                        </Flex>

                        <Flex mt={2} alignItems="center">
                            <Icon as={CalendarCheck} size={16} color="gray.500" mr={1} />
                            <Text fontSize="sm" color="gray.500" mr={4}>
                                Updated At: {formatDate(localEstimate.updated_at)}
                            </Text>
                        </Flex>

                        <Divider my={6} />

                        <Flex direction="column" p={4}>
                            <Text fontSize="lg" fontWeight="bold" mb={2}>
                                Estimate Details
                            </Text>

                            <Stack spacing={4}>
                                {/* Size Category */}
                                <Flex
                                    alignItems="center"
                                    border="1px"
                                    borderColor="gray.300"
                                    borderRadius="md"
                                    p={2}
                                >
                                    <Icon as={Layers} mr={2} color="gray.400" />
                                    <Text fontWeight="500" color="gray.500" fontSize="sm">
                                        Size Category: {localEstimate.size_category}
                                    </Text>
                                </Flex>

                                {/* Weight */}
                                <Flex
                                    alignItems="center"
                                    border="1px"
                                    borderColor="gray.300"
                                    borderRadius="md"
                                    p={2}
                                >
                                    <Icon as={BoxIcon} mr={2} color="gray.400" />
                                    {isEditing ? (
                                        <InputGroup size="sm">
                                            <Input
                                                type="number"
                                                value={formValues.weight}
                                                onChange={(e) => handleInputChange("weight", e.target.value)}
                                                placeholder="Weight"
                                            />
                                            <InputRightAddon children="kg" />
                                        </InputGroup>
                                    ) : (
                                        <Text fontWeight="500" color="gray.500" fontSize="sm">
                                            Weight: {localEstimate.weight} kg
                                        </Text>
                                    )}
                                </Flex>

                                {/* Dimensions */}
                                <Flex
                                    alignItems="center"
                                    border="1px"
                                    borderColor="gray.300"
                                    borderRadius="md"
                                    p={2}
                                >
                                    <Icon as={Clipboard} mr={2} color="gray.400" />
                                    {isEditing ? (
                                        <Flex gap={2}  wrap="wrap">
                                            <InputGroup size="sm" flex="1" minWidth="100px" maxWidth="125px">
                                                <Input
                                                    type="number"
                                                    value={formValues.length}
                                                    onChange={(e) => handleInputChange("length", e.target.value)}
                                                    placeholder="Length"
                                                />
                                           
                                            </InputGroup>

                                            <InputGroup size="sm" flex="1" minWidth="100px" maxWidth="125px">
                                                <Input
                                                    type="number"
                                                    value={formValues.width}
                                                    onChange={(e) => handleInputChange("width", e.target.value)}
                                                    placeholder="Width"
                                                />
                                               
                                            </InputGroup>

                                            <InputGroup size="sm" flex="1" minWidth="100px" maxWidth="125px">
                                                <Input
                                                    type="number"
                                                    value={formValues.height}
                                                    onChange={(e) => handleInputChange("height", e.target.value)}
                                                    placeholder="Height"
                                                />
                                                <InputRightAddon children="cm" />
                                            </InputGroup>
                                        </Flex>
                                    ) : (
                                        <Text fontWeight="500" color="gray.500" fontSize="sm">
                                            Dimensions: {localEstimate.length}cm × {localEstimate.width}cm × {localEstimate.height}cm
                                        </Text>
                                    )}
                                </Flex>
                            </Stack>

                            {/* EDIT / SAVE BUTTON */}
                            <Flex justifyContent="flex-end" mt={6}>
                                <GradientButton
                                    height='40px'
                                    fontSize='sm'
                                    onClick={handleToggleEdit}
                                    isLoading={loading}
                                    size="lg"
                                >
                                    {isEditing ? "Save" : "Edit"}
                                </GradientButton>
                            </Flex>
                        </Flex>
                    </Box>
                }
            />
        </Flex>
    );
};

export default ViewSizeEstimates;